/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

const STORAGE_KEY = 'impersonation'

interface ImpersonationState {
  employeeId: string
  employeeName: string
  roles: string[]
}

interface ImpersonationContextType {
  isImpersonating: boolean
  impersonatedEmployeeId: string | null
  impersonatedEmployeeName: string | null
  impersonatedRoles: string[]
  startImpersonating: (employeeId: number, employeeName: string, roles: string[]) => void
  stopImpersonating: () => void
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null)

function readStoredState(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function writeStoredState(state: ImpersonationState | null) {
  try {
    if (state) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // sessionStorage unavailable
  }
}

/**
 * Returns the current impersonation employee ID or null.
 * Works outside of React — call anytime to read the current override for fetch headers.
 */
export function getImpersonationOverride(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const state: ImpersonationState = JSON.parse(raw)
    return state.employeeId
  } catch {
    return null
  }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<ImpersonationState | null>(readStoredState)

  const startImpersonating = useCallback(
    (employeeId: number, employeeName: string, roles: string[]) => {
      const newState: ImpersonationState = {
        employeeId: String(employeeId),
        employeeName,
        roles,
      }
      writeStoredState(newState)
      setState(newState)
      queryClient.invalidateQueries()
    },
    [queryClient],
  )

  const stopImpersonating = useCallback(() => {
    writeStoredState(null)
    setState(null)
    queryClient.invalidateQueries()
  }, [queryClient])

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating: !!state,
        impersonatedEmployeeId: state?.employeeId ?? null,
        impersonatedEmployeeName: state?.employeeName ?? null,
        impersonatedRoles: state?.roles ?? [],
        startImpersonating,
        stopImpersonating,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext)
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider')
  }
  return context
}
